import { Box, Button, Dialog, Grid, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import './CardDetail.css';
import { contentProps } from './CardDetail.props';
import { trainingCoursesEventsDataSelector } from '../../redux/trainingCoursesSlice';
import { useAppSelector } from '../../redux/hooks';
import { HorizontalCard } from '../../components/HorizontalCard';
import { LeftCardCompleted } from '../../components/LeftCardCompleted';
import { IDataCourses } from '../../redux/trainingCoursesByDateSlice';
import ChangeWorkSpace from '../../hoc/changeWorkSpace';
import { LeftCardDetail } from '../../components/LeftCardDetail';
import CertificateArtwork from '../../assets/images/certificate-artwork-3.svg';
import { LoadingPage } from '../../components/LoadingPage';
import {
  trainingCourseDetailEvents,
  trainingCourseDetailEventsDataSelector
} from '../../redux/trainingCourseDetailSlice';
import CloseIcon from '@mui/icons-material/Close';
import './styles/mobile.css';
import './styles/ipad.css';
import moment from 'moment-timezone';
import {
  CATEGORY_TYPE,
  COURSE_POLICY_STATUS, COURSE_TAB,
  PARAMS_COURSE_STATUS,
  TYPE_LETF_CARD_DETAIL
} from '../../constants/constants';
import { convertUtcToTimezone, getCurrentTimeZone } from '../../utils/dateTimeHelper';
import { ETrainingLabel } from '../../constants/enums';
import { useDispatch } from 'react-redux';
import { workSpaceEventsDataSelector } from '../../redux/workSpaceSlice';
import { checkManagerTabs } from '../../utils/util';
import { TabsHeader } from '../../components/TabsHeader';
import {
  getAssignedLearningListEvents,
  getAssignedLearningListEventsDataSelector
} from '../../redux/getAssignedLearningListSilce';
import {
  coursePolicyEventsDataSelector,
  getCoursePolicyEvents
} from '../../redux/coursePolicySlice';
import { NoCourseDisplay } from '../../components/NoCourseDisplay';

const currentTimeZone = getCurrentTimeZone();
moment.tz.setDefault(currentTimeZone);
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: '15px 23px 30px 23px',
  textAlign: 'left',
  color: theme.palette.text.secondary,
  height: '94.5%'
}));

const CardDetailComponent: React.FC<contentProps> = () => {
  const currentTimeZone = getCurrentTimeZone();
  moment.tz.setDefault(currentTimeZone);

  const dispatch = useDispatch<any>();
  const [courseActive, setCourseActive] = useState(-1);
  const [openPopup, setOpenPopup] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));
  const { courseDetails, isLoading: isLoadingCourseDetails } = useAppSelector(
    trainingCourseDetailEventsDataSelector
  );
  const courseLoading = useAppSelector(trainingCourseDetailEventsDataSelector).isLoading;
  const [courseDetailData, setCourseDetailData] = useState<any>(courseDetails);
  const { workSpaceData = [] } = useAppSelector(workSpaceEventsDataSelector);
  const { isLoadingCoursesQuantity } = useAppSelector(trainingCoursesEventsDataSelector);

  const [courseStatusParam, setCourseStatusParam] = useState(PARAMS_COURSE_STATUS.PENDING);
  const { dataAssignedCourse, isLoading } = useAppSelector(
    getAssignedLearningListEventsDataSelector
  );
  const [tabIndex, setTabIndex] = useState<number>(0);
  const { coursePolicyData } = useAppSelector(coursePolicyEventsDataSelector);

  useEffect(() => {
    setCourseDetailData(courseDetails);
    if(courseDetails && (courseDetails?.course_policy_status === COURSE_POLICY_STATUS.MANDATORY || courseDetails?.course_policy_status === COURSE_POLICY_STATUS.ACCEPTED)){
      if(courseDetails?.campaign_category !== CATEGORY_TYPE.QUESTIONNAIRE) {
        dispatch(getCoursePolicyEvents(courseDetails?.training_result_id));
      }
    }
  }, [courseDetails]);

  const courseDetailHandler = (courseData: IDataCourses) => {
    const data = {
      student_id: courseData.student_id,
      campaign_id: courseData.campaign_id
    };
    dispatch(trainingCourseDetailEvents(data));
    setOpenPopup(isMobile);
  };

  const closeDetailHandler = () => {
    setOpenPopup(false);
    setCourseDetailData({});
  };

  useEffect(() => {
    if (workSpaceData.length > 0) {
      dispatch(getAssignedLearningListEvents(courseStatusParam));
    }
  }, [dispatch]);

  // sort for Questionnaire course to top
  const sortCustom = (arr: IDataCourses[]) => {
    // Define the priority order for "training_label" field
    // Lowest index = highest priority
    const priority = ["Outstanding", "Assigned", "Completed"];

    // Safe date parser → if value is missing/invalid, return +/-Infinity
    // so sorting will still work without crashing.
    const safeDate = (dateStr: string | undefined, fallback: "min" | "max") => {
      if (!dateStr) return fallback === "min" ? Infinity : -Infinity;
      const time = new Date(dateStr).getTime();
      return isNaN(time) ? (fallback === "min" ? Infinity : -Infinity) : time;
    };

    // Separate items with course_type is 'questionnaire' and keep others unchanged
    const questionnaires = arr.filter(item => item.course_type === 2);
    const nonQuestionnaires = arr.filter(item => item.course_type !== 2);

    // Sort only the "questionnaire" items according to the rules
    const sortedQuestionnaires = questionnaires.sort((a, b) => {
      // Find index of action in the priority list
      const aPriority = priority.indexOf(a.training_label);
      const bPriority = priority.indexOf(b.training_label);

      // If actions have different priorities → sort by priority order
      if (aPriority !== bPriority) return aPriority - bPriority;

      // If both items are "outstanding":
      // → sort by end_date ASC (earliest first)
      if (a.training_label === "Outstanding") {
        return safeDate(a.end_date, "min") - safeDate(b.end_date, "min");
      }

      // If both items are "Assigned" or "completed":
      // → sort by start_date DESC (latest first)
      if (a.training_label === "Assigned" || a.training_label === "Completed") {
        return safeDate(b.start_date, "max") - safeDate(a.start_date, "max");
      }

      // Default: keep order
      return 0;
    });

    // Return a new array:
    // 1. All questionnaire items sorted at the beginning
    // 2. All non-Questionnaire items after, in their original order
    return [...sortedQuestionnaires, ...nonQuestionnaires];
  };

  const durationToNow = (date?: string) =>
    Math.abs(moment(convertUtcToTimezone(date)).diff(moment()));

  const handleDuration = useCallback((array: IDataCourses[], type: string) => {
    let result: IDataCourses | undefined = undefined;

    if (array.length > 0) {
      result = array.reduce((accumulator, currentValue) => {
        if (type === ETrainingLabel.Assigned) {
          const durationCurrentDate = durationToNow(currentValue.start_date);
          const durationPreviousDate = durationToNow(accumulator.start_date);
          if (durationCurrentDate < durationPreviousDate) {
            accumulator = currentValue;
          }
        }
        if (type === ETrainingLabel.Outstanding) {
          if (moment(currentValue.end_date).utc().isBefore(moment(accumulator.end_date).utc())) {
            accumulator = currentValue;
          }
        }
        return accumulator;
      }, array[0]);
    }

    return result;
  }, []);

  const defaultDetailValue = useMemo(() => {
    if (dataAssignedCourse?.course_list && dataAssignedCourse.course_list.length > 0) {
      if (courseStatusParam == PARAMS_COURSE_STATUS.COMPLETED) {
        const courseList = sortCustom(dataAssignedCourse.course_list)
        return courseList[0];
      }
      const overdueArr: IDataCourses[] = [];
      const assignedArr: IDataCourses[] = [];
      dataAssignedCourse.course_list.forEach((item) => {
        if (item.training_label === ETrainingLabel.Outstanding) {
          overdueArr.push(item);
        }
        if (item.training_label === ETrainingLabel.Assigned) {
          assignedArr.push(item);
        }
      });
      if (overdueArr.length > 0) return handleDuration(overdueArr, ETrainingLabel.Outstanding);
      else return handleDuration(assignedArr, ETrainingLabel.Assigned);
    }
    return undefined;
  }, [dataAssignedCourse, handleDuration]);

  useEffect(() => {
    if (defaultDetailValue && defaultDetailValue.campaign_id) {
      setCourseActive(defaultDetailValue.campaign_id);
      const params = {
        student_id: defaultDetailValue.student_id,
        campaign_id: defaultDetailValue.campaign_id
      };
      dispatch(trainingCourseDetailEvents(params));
    } else {
      setCourseActive(-1);
    }
  }, [dispatch, dataAssignedCourse, defaultDetailValue]);

  const noValueComponent = (status: string) => {
    switch (status) {
      case PARAMS_COURSE_STATUS.PENDING:
        return <NoCourseDisplay contents="No pending courses available." />;
        break;
      case PARAMS_COURSE_STATUS.COMPLETED:
        return <NoCourseDisplay contents="No completed courses available." />;
        break;
      default:
        return <></>;
        break;
    }
  };

  const listAssignedLearningComponent = (params: any[]) => {
    const sortedParams = sortCustom(params)
    return (
      <Box component={'div'} className={'box-list'}>
        {sortedParams.length > 0
          ? sortedParams.map((course, index: number) => {
              return (
                <HorizontalCard
                  horizontalCardData={course}
                  key={index}
                  className={courseActive === course.campaign_id ? 'active' : ''}
                  onSelected={() => setCourseActive(course?.campaign_id ?? -1)}
                  clickToDetail={() => courseDetailHandler(course)}
                />
              );
            })
          : !isLoading
            ? noValueComponent(courseStatusParam)
            : null}
        <Dialog
          open={openPopup}
          onClose={closeDetailHandler}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          className={'modal-dialog-course-detail'}
          fullScreen={true}
        >
          <Box className={'modal-wrapper'}>
            <div className={'exit-block-wrapper'}>
              <Button className={'exit-button'} onClick={closeDetailHandler}>
                <CloseIcon className={'close-icon'} />
              </Button>
            </div>
            <div className={'left-card-detail-mobile'}>
              <LeftCardDetail
                leftCardDetailsData={courseDetailData}
                type={TYPE_LETF_CARD_DETAIL.ASSIGNED_LEARNING}
                coursePolicyUrl={coursePolicyData.coursePolicyUrl}
              />
            </div>
          </Box>
        </Dialog>
      </Box>
    );
  };

  const dataTabs: any = [
    {
      label: COURSE_TAB.PENDING,
      component: listAssignedLearningComponent(dataAssignedCourse?.course_list),
      number: dataAssignedCourse?.pending_status || 0
    },
    {
      label: COURSE_TAB.COMPLETED,
      component: listAssignedLearningComponent(dataAssignedCourse?.course_list),
      number: dataAssignedCourse?.completed_status || 0
    }
  ];

  const handleTabClick = (val: any) => {
    setTabIndex(val);
    switch (val) {
      case 0:
        setCourseStatusParam(PARAMS_COURSE_STATUS.PENDING);
        dispatch(getAssignedLearningListEvents(PARAMS_COURSE_STATUS.PENDING));
        break;
      case 1:
        setCourseStatusParam(PARAMS_COURSE_STATUS.COMPLETED);
        dispatch(getAssignedLearningListEvents(PARAMS_COURSE_STATUS.COMPLETED));
        break;
      default:
        setCourseStatusParam(PARAMS_COURSE_STATUS.PENDING);
        dispatch(getAssignedLearningListEvents(PARAMS_COURSE_STATUS.PENDING));
        break;
    }
  };

  return (
    <>
      <Box className={'card-detail'}>
        <Grid container spacing={'20px'}>
          <Grid item xs={12} sm={12} tablet={12} laptop={5} desktop={5}>
            {/*right detail*/}
            <Box component={'div'} className={'box-list-wrapper'}>
              <TabsHeader
                {...checkManagerTabs(dataTabs, tabIndex)}
                key={'course-list-tabs'}
                onClick={handleTabClick}
                isMainBox={true}
                labelBox={'Course List'}
                messageTooltipBox={'Course List'}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            tablet={12}
            laptop={7}
            desktop={7}
            className={'is-mobile-course-detail'}>
            {/*All Completed Course Component*/}
            {courseActive === -1 ? (
              <LeftCardCompleted
                image={CertificateArtwork}
                contents={
                  tabIndex === 1
                    ? 'You have not completed any courses.'
                    : 'You have no courses currently assigned.'
                }
                sub_content={
                  tabIndex === 1
                    ? 'Please complete any course assigned to you under the Pending tab.'
                    : 'Not to worry though, you’re able to revisit the courses you previously completed in the Course Catalogue tab above.'
                }
                styleText={{ maxWidth: '530px' }}
              />
            ) : (
              <Item className={'wrapper-detail'}>
                <LeftCardDetail
                  leftCardDetailsData={!isLoadingCourseDetails ? courseDetails : {}}
                  type={TYPE_LETF_CARD_DETAIL.ASSIGNED_LEARNING}
                  coursePolicyUrl={coursePolicyData.coursePolicyUrl}
                />
              </Item>
            )}
          </Grid>
        </Grid>
      </Box>
      <LoadingPage open={(isLoading || courseLoading) && !isLoadingCoursesQuantity} />
    </>
  );
};
export const CardDetail = ChangeWorkSpace(CardDetailComponent);
